import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { isAuthenticated } from '@/lib/auth';
import { apiClient } from '@/lib/api-client';

interface BankAccount {
  accountNumber: string;
  accountName?: string;
  networkId: string;
}

interface AddressStats {
  totalDeposits: number;
  totalVolume: number;
  lastDepositAt: string | null;
  currentBalance: number | null;
}

interface StaticAddress {
  id: string;
  depositAddress: string;
  originNetwork: string;
  smartAccountAddress: string;
  destinationHash: string;
  createdAt: string;
  lastActivityAt: string | null;
  status: 'active' | 'inactive';
  bankAccount: BankAccount;
  currency: string;
  countryCode: string;
  stats: AddressStats;
}

export default function StaticAddresses() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<StaticAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadAddresses();
  }, [navigate, filter]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('limit', '50');
      params.append('offset', '0');
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await apiClient.get(`/static-addresses?${params}`);
      setAddresses(response.data.addresses);
      setPagination(response.data.pagination);
    } catch (error: any) {
      console.error('Failed to load static addresses:', error);
      setError(error.response?.data?.message || 'Failed to load static addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (depositAddress: string) => {
    setSyncing({ ...syncing, [depositAddress]: true });
    setError(null);

    try {
      const response = await apiClient.post(`/static-addresses/${depositAddress}/sync`);

      // Show success message
      const { processedDeposits, message } = response.data;

      // Refresh the addresses list to show updated stats
      await loadAddresses();

      // You could show a toast notification here
      console.log(`Sync completed: ${message}`);

    } catch (error: any) {
      console.error('Failed to sync address:', error);
      setError(error.response?.data?.message || 'Failed to sync address');
    } finally {
      setSyncing({ ...syncing, [depositAddress]: false });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could show a toast notification here
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number | null, currency: string = 'USDC') => {
    if (amount === null) return '-';
    return `${amount.toFixed(2)} ${currency}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-muted-foreground">Loading addresses...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Static Offramp Addresses</h1>
          <p className="text-muted-foreground mt-2">
            Manage your static deposit addresses for crypto offramp
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            size="sm"
          >
            All Addresses
          </Button>
          <Button
            variant={filter === 'active' ? 'default' : 'outline'}
            onClick={() => setFilter('active')}
            size="sm"
          >
            Active
          </Button>
          <Button
            variant={filter === 'inactive' ? 'default' : 'outline'}
            onClick={() => setFilter('inactive')}
            size="sm"
          >
            Inactive
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Addresses List */}
        {addresses.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">
                No static addresses found. Create addresses via the API.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {addresses.map((address) => (
              <Card key={address.id} className={address.status === 'inactive' ? 'opacity-75' : ''}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {address.originNetwork}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          address.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {address.status}
                        </span>
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Created: {formatDate(address.createdAt)}
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => handleSync(address.depositAddress)}
                      disabled={syncing[address.depositAddress]}
                      size="sm"
                      variant="outline"
                    >
                      {syncing[address.depositAddress] ? 'Syncing...' : 'Sync'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Deposit Address */}
                  <div>
                    <label className="text-sm font-medium text-gray-600">Deposit Address</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-50 px-3 py-2 rounded text-sm flex-1 font-mono">
                        {address.depositAddress}
                      </code>
                      <Button
                        onClick={() => copyToClipboard(address.depositAddress)}
                        size="sm"
                        variant="ghost"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  {/* Bank Account */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Bank Account</label>
                      <p className="text-sm mt-1">
                        {address.bankAccount.accountNumber} ({address.bankAccount.networkId})
                      </p>
                      {address.bankAccount.accountName && (
                        <p className="text-sm text-gray-500">{address.bankAccount.accountName}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Settlement Currency</label>
                      <p className="text-sm mt-1">{address.currency} ({address.countryCode})</p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4 pt-2 border-t">
                    <div>
                      <label className="text-xs font-medium text-gray-600">Total Deposits</label>
                      <p className="text-lg font-semibold">{address.stats.totalDeposits}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Total Volume</label>
                      <p className="text-lg font-semibold">{formatCurrency(address.stats.totalVolume)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Current Balance</label>
                      <p className="text-lg font-semibold">
                        {formatCurrency(address.stats.currentBalance)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Last Deposit</label>
                      <p className="text-sm">{formatDate(address.stats.lastDepositAt)}</p>
                    </div>
                  </div>

                  {/* Smart Account (collapsible details) */}
                  <details className="text-sm">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                      Technical Details
                    </summary>
                    <div className="mt-2 space-y-2 text-xs">
                      <div>
                        <span className="font-medium">Smart Account:</span>{' '}
                        <code className="bg-gray-50 px-1 rounded">{address.smartAccountAddress}</code>
                      </div>
                      <div>
                        <span className="font-medium">Destination Hash:</span>{' '}
                        <code className="bg-gray-50 px-1 rounded">{address.destinationHash}</code>
                      </div>
                      <div>
                        <span className="font-medium">Last Activity:</span>{' '}
                        {formatDate(address.lastActivityAt)}
                      </div>
                    </div>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.hasMore && (
          <div className="flex justify-center">
            <Button variant="outline">Load More</Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}